import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { SharedApiService, ApiType } from '../../../../../libs/shared/src/lib/services/api.service';
import { HttpParams } from '@angular/common/http';
import { Longcv, Resume } from '../../../../../libs/shared/src/lib/models/dashboard/user-from-be';
import { ConsentItem } from '../../../../../libs/shared/src/lib/models/consent.model';

// Interface for job attachments (CV, cover letter, other documents)
// Matches backend UserAttachments model - only required fields
export interface JobAttachment {
  url: string;
  fileType: string;
  fileName: string;
  type?: string; // Document type: cv, coverLetter, certificate, portfolio, other
}

// Interface for registered user application (minimal data)
export class RegisteredUserApplication {
  jobApplicationConsent: boolean; // Keep for backward compatibility with form
  consents?: ConsentItem[]; // OpenAPI-compatible consent array
  resumeAttachment?: Resume;
  jobAttachments?: JobAttachment[]; // Additional files (CV, cover letter, etc.)
  userLanguage: string;
}

// Interface for non-registered user application (full profile data)
export class NonRegisteredUserApplication extends RegisteredUserApplication {
  email: string;
  profile: {
    title: string;
    titleAfterName: string;
    firstName: string;
    lastName: string;
    gender: string;
    email: string;
    telephoneNumber: string;
    dateOfBirth: string;
    address: {
      formattedAddress?: string;
      streetName: string;
      streetNumber: string;
      city: string;
      administrativeArea?: string;
      postalCode: string;
      country: string;
      latitude: number;
      longitude: number;
    };
    longcv?: Longcv;
  };
}

// API Response interface
interface ApplicationResponse {
  status: number;
  message: string;
  body?: any;
}

@Injectable({
  providedIn: 'root',
})
export class JobApplicationService {
  constructor(private readonly sharedApiService: SharedApiService) {}

  /**
   * Submit job application for registered user (minimal data)
   * @param applicationData - Minimal application data for logged-in users
   */
  submitRegisteredUserApplication(applicationData: RegisteredUserApplication, queryParams: HttpParams): Observable<ApplicationResponse> {
    return this.sharedApiService.post<ApplicationResponse>('api/job-applications/registered', applicationData, queryParams, {
      apiType: ApiType.OPEN,
      jobsRequest: true,
    });
  }

  /**
   * Submit job application for non-registered user (full profile data)
   * @param applicationData - Complete application data for non-logged-in users
   */
  submitNonRegisteredUserApplication(applicationData: NonRegisteredUserApplication, queryParams: HttpParams): Observable<ApplicationResponse> {
    // Transform the data to match backend API expectations
    const transformedData = this.transformNonRegisteredApplicationForBackend(applicationData);

    return this.sharedApiService.post<ApplicationResponse>('api/job-applications/non-registered', transformedData, queryParams, {
      apiType: ApiType.OPEN,
      jobsRequest: true,
    });
  }

  /**
   * Transform frontend application data to backend API format
   * - Renames zipCode to postalCode
   * - Ensures streetNumber and postalCode are integers
   * - Removes spaces from postal codes
   * - Removes empty resumeAttachment if not provided
   */
  private transformNonRegisteredApplicationForBackend(data: NonRegisteredUserApplication): any {
    const transformed = { ...data };

    // Remove resumeAttachment if it's empty or not provided
    if (transformed.resumeAttachment) {
      const resume = transformed.resumeAttachment;
      const isEmpty = !resume.resumeFile && !resume.url && !resume.attachmentId;
      if (isEmpty) {
        delete transformed.resumeAttachment;
      }
    }

    if (transformed.profile?.address) {
      const address = transformed.profile.address;

      // Create new address object with backend field names (same as registered user)
      const backendAddress: any = {
        formattedAddress: address.formattedAddress,
        streetName: address.streetName,
        city: address.city,
        administrativeArea: address.administrativeArea,
        country: address.country,
        latitude: address.latitude,
        longitude: address.longitude,
      };

      // Keep streetNumber as string if present
      if (address.streetNumber !== undefined && address.streetNumber !== null) {
        backendAddress.streetNumber = address.streetNumber;
      }

      // Keep postalCode as string if present
      if (address.postalCode !== undefined && address.postalCode !== null) {
        backendAddress.postalCode = address.postalCode;
      }

      transformed.profile.address = backendAddress;
    }

    return transformed;
  }

  /**
   * Submit job application - automatically chooses the right endpoint based on data type
   * @param applicationData - Application data (can be either type)
   * @param isRegisteredUser - Whether the user is logged in
   */
  submitApplication(
    applicationData: RegisteredUserApplication | NonRegisteredUserApplication,
    isRegisteredUser: boolean,
    jobId: string,
    userId?: number
  ): Observable<ApplicationResponse> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('jobOfferID', jobId);
    if (userId && userId > 0) {
      queryParams = queryParams.append('userId', userId.toString());
    }
    if (isRegisteredUser) {
      return this.submitRegisteredUserApplication(applicationData as RegisteredUserApplication, queryParams);
    } else {
      return this.submitNonRegisteredUserApplication(applicationData as NonRegisteredUserApplication, queryParams);
    }
  }
}
